import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useUserProfile } from "../../app";
import PropertyAPI from "../../api/property/property-api-factory";
import { ImageGalleryElement } from "../../components/reusable-elements/image-gallery-element";
import "../../components/properties/gallery.css";

export const PropertyBrochurePrint = () => {
    const { id } = useParams();
    const { instance, inProgress, accounts } = useMsal();
    const userProfile = useUserProfile();
    const api = useMemo(
        () => new PropertyAPI(instance, inProgress, accounts, true),
        [instance, inProgress, accounts]
    );
    const [propertyDocument, setPropertyDocument] = useState(null);

    useEffect(() => {
        api.get(id).then(data => setPropertyDocument(data));
    }, [api, id]);

    if (!propertyDocument) {
        return null;
    }

    const address = propertyDocument?.property?.address || {};
    const firstLine = [address?.doorNumberOrName, address?.roadName]
        .filter(Boolean)
        .join(" ")
        .trim();
    const propertyAddressLabel = [firstLine, address?.postalCode]
        .filter(Boolean)
        .join(", ")
        .trim();

    const rooms = propertyDocument?.property?.rooms ?? [];

    const authorName = userProfile?.systemProfile?.displayName
        ?? userProfile?.graphMe?.displayName
        ?? "";

    const authorJobTitle = userProfile?.systemProfile?.jobTitle
        ?? userProfile?.graphMe?.jobTitle
        ?? "";

    const authorImage = userProfile?.systemProfile?.thumbnailProfileImageIncludingSasToken
        ?? userProfile?.aadProfileImage
        ?? "/assets/img/user/user-12.jpg";

    const propertyDescription = (typeof propertyDocument?.property?.description === "string"
        ? propertyDocument.property.description.trim()
        : "") || "";

    const photos = propertyDocument.property.photoGallery ?? [];
    const primaryImage = (Array.isArray(photos) ? photos : [])
        .find(p => p?.isPrimaryImage && (p.pathIncSASToken || p.thumbPathIncSASToken))
        || (Array.isArray(photos) ? photos : [])
            .find(p => p && (p.pathIncSASToken || p.thumbPathIncSASToken));

    const sortedRooms = [...rooms].sort((a, b) => {
        const aType = a?.roomType || "";
        const bType = b?.roomType || "";
        const aIsBedroom = aType === "Bedroom";
        const bIsBedroom = bType === "Bedroom";
        const aNum = typeof a?.roomNumber === "number" ? a.roomNumber : parseInt(a?.roomNumber, 10);
        const bNum = typeof b?.roomNumber === "number" ? b.roomNumber : parseInt(b?.roomNumber, 10);
        const aHasNum = aIsBedroom && !Number.isNaN(aNum);
        const bHasNum = bIsBedroom && !Number.isNaN(bNum);

        if (aHasNum && bHasNum) return aNum - bNum;
        if (aHasNum && !bHasNum) return -1;
        if (!aHasNum && bHasNum) return 1;

        if (aIsBedroom && !bIsBedroom) return -1;
        if (!aIsBedroom && bIsBedroom) return 1;

        if (aType !== bType) return aType.localeCompare(bType);
        const aId = a?.id || a?._id || a?.roomId || "";
        const bId = b?.id || b?._id || b?.roomId || "";
        return String(aId).localeCompare(String(bId));
    });

    const buildRoomLabel = (room) => {
        const type = room?.roomType || "Room";
        if (type === "Bedroom") {
            return room?.roomNumber ? `Bedroom ${room.roomNumber}` : "Bedroom";
        }
        return type;
    };

    const renderQuoteBlock = (text, extraClassName = "") => {
        const content = typeof text === "string" ? text.trim() : "";
        if (!content) return null;

        const containerClass = ["quote-container", extraClassName].filter(Boolean).join(" ");

        return (
            <div className={containerClass}>
                <div className="quote">
                    <i className="fa fa-quote-left" />
                    <h3>{content}</h3>
                    <i className="fa fa-quote-right" />
                </div>
                {authorName && (
                    <div className="info-block">
                        <div className="image">
                            <img
                                src={authorImage}
                                alt={authorName}
                            />
                        </div>
                        <div className="info">
                            {authorName}
                            {authorJobTitle && (
                                <small>{authorJobTitle}</small>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="brochure-print-page">
            <div className="d-print-none mb-3">
                <Link
                    to={`/property/detail/${id}`}
                    className="btn btn-sm btn-outline-secondary"
                >
                    Back to property details
                </Link>
            </div>
            <div className="gallery-container">
                <h1 className="page-header">
                    <strong>Property Details</strong>
                    {propertyAddressLabel ? (
                        <> | <small>{propertyAddressLabel}</small></>
                    ) : null}
                </h1>
                {(propertyDescription || primaryImage) && (
                    <>
                        <div className="mb-4 brochure-header-block">
                            <div className="brochure-main-image-wrapper">
                                {primaryImage && (
                                    <div className="brochure-main-image-frame">
                                        <img
                                            src={primaryImage.pathIncSASToken || primaryImage.thumbPathIncSASToken}
                                            alt={primaryImage.title || "Main property photo"}
                                            className="brochure-main-image"
                                        />
                                    </div>
                                )}
                            </div>
                            {propertyDescription && renderQuoteBlock(propertyDescription, "brochure-header-quote")}
                        </div>
                    </>
                )}

                <div className="mb-4 brochure-gallery-section">
                    <h2 className="mb-3">Gallery</h2>
                    <ImageGalleryElement
                        key="photo-gallery-images-print"
                        photos={photos}
                        enableSlideshow={false}
                        preferFullRes
                    />
                </div>

                <div>
                    <h2 className="mb-3">Rooms</h2>
                    {sortedRooms.map((room, index) => {
                        const gallery = Array.isArray(room?.mediaGallery) ? room.mediaGallery : [];
                        const label = buildRoomLabel(room);
                        const roomKey = room?.id || room?._id || room?.roomId || `room-${index}`;
                        const blockClass = index > 0 ? "pt-3 mt-3 border-top" : "pt-1";

                        const description = typeof room.description === "string" ? room.description : "";

                        const hasMedia = Array.isArray(gallery) && gallery.length > 0;
                        const hasDescription = !!(description && description.trim());
                        if (!hasMedia && !hasDescription) return null;

                        return (
                            <div key={roomKey} className={blockClass}>
                                <h4 className="mb-2">{label}</h4>
                                <div className="brochure-room-block">
                                    {hasMedia && (
                                        <div className="brochure-room-image-wrapper">
                                            <ImageGalleryElement
                                                key={`room-media-print-${roomKey}`}
                                                photos={gallery}
                                                enableSlideshow={false}
                                                getImageContextLabel={() => label}
                                            />
                                        </div>
                                    )}
                                    {hasDescription && renderQuoteBlock(description, "mb-3 brochure-room-quote")}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
